// screens/AdminAmlReviewScreen.js
// ============================================================================
// AML Review Dashboard (Task #7)
// Admin-only screen for reviewing flagged accounts from amlDetection.runAmlChecks.
// Shows flagged users with their recent AML flags; each flag has Clear /
// Confirm Suspicious / Permanent Ban actions. Anti-tipping-off rule lives
// in the user-facing path (createWithdrawal returns a generic message) —
// THIS screen shows admins the full flag detail for review purposes.
// ============================================================================

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  SafeAreaView, View, Text, StyleSheet, TouchableOpacity, Pressable,
  ActivityIndicator, Alert, ScrollView, TextInput, Modal,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { httpsCallable, getFunctions } from 'firebase/functions';

import Header from '../components/Header';
import Footer from '../components/Footer';
import NavBar from '../components/NavBar';

const COLORS = {
  bg: '#000000',
  card: '#111111',
  cardAlt: '#0a0a0a',
  border: '#1f1f1f',
  borderLight: '#2a2a2a',
  primary: '#FCD000',
  success: '#22c55e',
  warning: '#f59e0b',
  danger: '#ef4444',
  info: '#3b82f6',
  text: '#ffffff',
  textMuted: '#888888',
  textDim: '#555555',
};

const PATTERN_LABELS = {
  pattern_1: 'Cash-Out Velocity',
  pattern_2: 'Low Play Ratio (30d)',
  pattern_3: 'Stake-Size Mismatch',
  pattern_4: 'Repeated Cycles',
};

const RESOLUTION_LABELS = {
  cleared: { label: 'Cleared', color: COLORS.success },
  confirmed_suspicious: { label: 'Confirmed Suspicious', color: COLORS.warning },
  permanent_ban: { label: 'Permanent Ban', color: COLORS.danger },
};

const formatCurrency = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;
const formatDate = (ms) => ms ? new Date(ms).toLocaleString('en-US', {
  month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
}) : '-';

export default function AdminAmlReviewScreen({ navigation }) {
  const auth = getAuth();
  const functions = getFunctions();

  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [guardLoading, setGuardLoading] = useState(true);

  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'flagged' | 'suspended_review'

  // Action modal state
  const [actionFlag, setActionFlag] = useState(null); // { flagId, userId, patternMatched, suggestedResolution }
  const [actionNotes, setActionNotes] = useState('');
  const [actionSubmitting, setActionSubmitting] = useState(false);
  const [banArmed, setBanArmed] = useState(false); // two-tap confirm for Permanent Ban

  // ============================================================================
  // ADMIN GUARD (mirrors AdminToolsScreen pattern)
  // ============================================================================
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!u) {
        setUser(null);
        setIsAdmin(false);
        setGuardLoading(false);
        navigation.replace('Landing');
        return;
      }
      setUser(u);
      try {
        const token = await u.getIdTokenResult(true);
        const claimAdmin = token?.claims?.role === 'admin';
        const snap = await getDoc(doc(db, 'users', u.uid));
        const docAdmin = snap.exists() && snap.data()?.role === 'admin';
        setIsAdmin(claimAdmin || docAdmin);
      } catch {
        setIsAdmin(false);
      } finally {
        setGuardLoading(false);
      }
    });
    return unsub;
  }, [auth, navigation]);

  // ============================================================================
  // DATA FETCHING
  // ============================================================================
  const fetchFlagged = useCallback(async () => {
    setLoading(true);
    try {
      const fn = httpsCallable(functions, 'adminGetFlaggedAccounts');
      const result = await fn(statusFilter === 'all' ? {} : { status: statusFilter });
      setAccounts(result.data?.accounts || []);
    } catch (e) {
      console.error('[aml-admin] failed to load flagged accounts:', e);
      if (typeof window !== 'undefined' && window.alert) {
        window.alert(`Failed to load flagged accounts: ${e.message}`);
      }
      setAccounts([]);
    } finally {
      setLoading(false);
    }
  }, [functions, statusFilter]);

  useEffect(() => {
    if (isAdmin) fetchFlagged();
  }, [isAdmin, fetchFlagged]);

  // ============================================================================
  // ACTION HANDLERS
  // ============================================================================
  const submitResolution = async (resolution) => {
    if (!actionFlag) return;
    setActionSubmitting(true);
    try {
      const fn = httpsCallable(functions, 'adminReviewAmlFlag');
      await fn({
        flagId: actionFlag.flagId,
        resolution,
        notes: actionNotes || null,
      });
      setActionFlag(null);
      setActionNotes('');
      setBanArmed(false);
      await fetchFlagged();
    } catch (e) {
      // Alert.alert is unreliable on web — surface error inline via console
      // and a window.alert which DOES work on web reliably.
      console.error('[aml-admin] resolution failed:', e);
      if (typeof window !== 'undefined' && window.alert) {
        window.alert(`Resolution failed: ${e.message}`);
      }
    } finally {
      setActionSubmitting(false);
    }
  };

  // Two-tap confirmation for Permanent Ban specifically — irreversible action
  // gets a second click to confirm. Clear and Confirm Suspicious are
  // single-click since they're reversible (Clear) or just an audit-trail
  // update (Confirm Suspicious). The Review modal itself is already the
  // primary confirmation step — the reviewer saw flag details and typed
  // notes before getting here.
  //
  // Avoiding Alert.alert here: it's unreliable on React Native Web for
  // multi-button confirmations (onPress callbacks frequently don't fire),
  // which is what was making these buttons appear dead.
  const handleAction = (resolution) => {
    if (resolution === 'permanent_ban' && !banArmed) {
      setBanArmed(true);
      return;
    }
    submitResolution(resolution);
  };

  // ============================================================================
  // RENDER
  // ============================================================================
  if (guardLoading) {
    return (
      <SafeAreaView style={styles.safeArea}>
        <View style={styles.center}>
          <ActivityIndicator color={COLORS.primary} />
          <Text style={styles.dim}>Checking admin permissions...</Text>
        </View>
      </SafeAreaView>
    );
  }

  if (!isAdmin) {
    return (
      <SafeAreaView style={styles.safeArea}>
        <View style={styles.center}>
          <Ionicons name="lock-closed" size={48} color={COLORS.danger} />
          <Text style={styles.warn}>Admin access required.</Text>
          <TouchableOpacity
            onPress={() => (navigation.canGoBack() ? navigation.goBack() : navigation.navigate('Lobby'))}
            style={styles.backBtn}
          >
            <Ionicons name="arrow-back" size={16} color={COLORS.textMuted} />
            <Text style={styles.backText}>Go Back</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  const totalFlagged = accounts.filter(a => a.amlStatus === 'flagged').length;
  const totalSuspended = accounts.filter(a => a.amlStatus === 'suspended_review').length;

  return (
    <SafeAreaView style={styles.safeArea}>
      <Header navigation={navigation} />

      <ScrollView contentContainerStyle={styles.container}>
        <View style={styles.titleRow}>
          <View style={{ flex: 1 }}>
            <Text style={styles.title}>AML Review Dashboard</Text>
            <Text style={styles.subtitle}>
              {totalSuspended} suspended · {totalFlagged} flagged · {accounts.length} total
            </Text>
          </View>
          <TouchableOpacity onPress={fetchFlagged} style={styles.refreshBtn} disabled={loading}>
            {loading ? <ActivityIndicator color={COLORS.primary} size="small" /> : (
              <Ionicons name="refresh" size={20} color={COLORS.primary} />
            )}
          </TouchableOpacity>
        </View>

        {/* Filter tabs */}
        <View style={styles.filterRow}>
          {[
            { key: 'all', label: 'All' },
            { key: 'suspended_review', label: 'Suspended' },
            { key: 'flagged', label: 'Flagged' },
          ].map(opt => (
            <Pressable
              key={opt.key}
              onPress={() => setStatusFilter(opt.key)}
              style={[styles.filterChip, statusFilter === opt.key && styles.filterChipActive]}
            >
              <Text style={[styles.filterChipText, statusFilter === opt.key && styles.filterChipTextActive]}>
                {opt.label}
              </Text>
            </Pressable>
          ))}
        </View>

        {loading && accounts.length === 0 ? (
          <View style={styles.emptyCard}>
            <ActivityIndicator color={COLORS.primary} />
            <Text style={styles.dim}>Loading flagged accounts...</Text>
          </View>
        ) : accounts.length === 0 ? (
          <View style={styles.emptyCard}>
            <Ionicons name="checkmark-circle" size={48} color={COLORS.success} />
            <Text style={styles.emptyTitle}>No flagged accounts</Text>
            <Text style={styles.dim}>Nothing to review right now.</Text>
          </View>
        ) : (
          accounts.map(account => (
            <AccountCard
              key={account.userId}
              account={account}
              onActionFlag={(flag) => {
                setActionFlag({
                  flagId: flag.id,
                  userId: account.userId,
                  patternMatched: flag.patternMatched,
                });
                setActionNotes('');
              }}
            />
          ))
        )}
      </ScrollView>

      {/* Action modal */}
      <Modal
        visible={!!actionFlag}
        transparent
        animationType="fade"
        onRequestClose={() => !actionSubmitting && setActionFlag(null)}
      >
        <View style={styles.modalBackdrop}>
          <View style={styles.modalCard}>
            <Text style={styles.modalTitle}>
              Resolve {actionFlag ? PATTERN_LABELS[actionFlag.patternMatched] || actionFlag.patternMatched : ''}
            </Text>
            <Text style={styles.dim}>Flag ID: {actionFlag?.flagId}</Text>
            <Text style={[styles.dim, { marginBottom: 12 }]}>User: {actionFlag?.userId}</Text>

            <Text style={styles.label}>Reviewer notes (optional)</Text>
            <TextInput
              value={actionNotes}
              onChangeText={setActionNotes}
              placeholder="Why are you taking this action?"
              placeholderTextColor={COLORS.textDim}
              style={styles.notesInput}
              multiline
              editable={!actionSubmitting}
            />

            {actionSubmitting ? (
              <View style={styles.center}>
                <ActivityIndicator color={COLORS.primary} />
                <Text style={styles.dim}>Saving...</Text>
              </View>
            ) : (
              <View style={styles.actionBtnRow}>
                <Pressable
                  onPress={() => handleAction('cleared')}
                  style={({ hovered, pressed }) => [
                    styles.actionBtn,
                    { backgroundColor: COLORS.success },
                    hovered && styles.actionBtnHovered,
                    pressed && styles.actionBtnPressed,
                  ]}
                >
                  <Text style={styles.actionBtnText}>Clear (false positive)</Text>
                </Pressable>
                <Pressable
                  onPress={() => handleAction('confirmed_suspicious')}
                  style={({ hovered, pressed }) => [
                    styles.actionBtn,
                    { backgroundColor: COLORS.warning },
                    hovered && styles.actionBtnHovered,
                    pressed && styles.actionBtnPressed,
                  ]}
                >
                  <Text style={styles.actionBtnText}>Confirm Suspicious</Text>
                </Pressable>
                <Pressable
                  onPress={() => handleAction('permanent_ban')}
                  style={({ hovered, pressed }) => [
                    styles.actionBtn,
                    { backgroundColor: COLORS.danger },
                    hovered && styles.actionBtnHovered,
                    pressed && styles.actionBtnPressed,
                  ]}
                >
                  <Text style={styles.actionBtnText}>
                    {banArmed ? 'Tap again to confirm BAN' : 'Permanent Ban'}
                  </Text>
                </Pressable>
              </View>
            )}

            <Pressable
              onPress={() => {
                if (actionSubmitting) return;
                setActionFlag(null);
                setActionNotes('');
                setBanArmed(false);
              }}
              style={({ hovered }) => [styles.cancelBtn, hovered && { opacity: 0.7 }]}
              disabled={actionSubmitting}
            >
              <Text style={styles.cancelBtnText}>Cancel</Text>
            </Pressable>
          </View>
        </View>
      </Modal>

      <Footer navigation={navigation} />
      <NavBar navigation={navigation} />
    </SafeAreaView>
  );
}

// ============================================================================
// ACCOUNT CARD
// ============================================================================
function AccountCard({ account, onActionFlag }) {
  const statusColor = account.amlStatus === 'suspended_review'
    ? COLORS.danger
    : account.amlStatus === 'flagged' ? COLORS.warning : COLORS.textMuted;

  const unresolvedFlags = account.flags.filter(f => !f.resolution);
  const resolvedFlags = account.flags.filter(f => f.resolution);

  return (
    <View style={styles.accountCard}>
      <View style={styles.accountHeader}>
        <View style={{ flex: 1 }}>
          <Text style={styles.accountName}>
            {account.displayName || account.email || account.userId}
          </Text>
          <Text style={styles.dim}>{account.email}</Text>
          <Text style={styles.dim}>UID: {account.userId}</Text>
        </View>
        <View style={styles.accountStats}>
          <View style={[styles.statusPill, { backgroundColor: statusColor }]}>
            <Text style={styles.statusPillText}>{account.amlStatus}</Text>
          </View>
          {account.banned && (
            <View style={[styles.statusPill, { backgroundColor: COLORS.danger, marginTop: 4 }]}>
              <Text style={styles.statusPillText}>BANNED</Text>
            </View>
          )}
          <Text style={[styles.dim, { marginTop: 4 }]}>{account.amlFlagsCount} flag(s)</Text>
        </View>
      </View>

      <View style={styles.kpiRow}>
        <Kpi label="Balance" value={formatCurrency(account.balanceCents)} />
        <Kpi label="Matches" value={account.matchesPlayedCount} />
        <Kpi label="Last flag" value={formatDate(account.lastAmlFlagAt)} />
      </View>

      {unresolvedFlags.length > 0 && (
        <View style={styles.flagsSection}>
          <Text style={styles.sectionLabel}>Unresolved flags</Text>
          {unresolvedFlags.map(flag => (
            <FlagRow key={flag.id} flag={flag} onAction={() => onActionFlag(flag)} />
          ))}
        </View>
      )}

      {resolvedFlags.length > 0 && (
        <View style={styles.flagsSection}>
          <Text style={[styles.sectionLabel, { color: COLORS.textDim }]}>
            Resolved ({resolvedFlags.length})
          </Text>
          {resolvedFlags.slice(0, 3).map(flag => (
            <FlagRow key={flag.id} flag={flag} />
          ))}
          {resolvedFlags.length > 3 && (
            <Text style={styles.dim}>+{resolvedFlags.length - 3} more</Text>
          )}
        </View>
      )}
    </View>
  );
}

function Kpi({ label, value }) {
  return (
    <View style={styles.kpi}>
      <Text style={styles.kpiLabel}>{label}</Text>
      <Text style={styles.kpiValue}>{value}</Text>
    </View>
  );
}

function FlagRow({ flag, onAction }) {
  const resolution = flag.resolution ? RESOLUTION_LABELS[flag.resolution] : null;
  return (
    <View style={styles.flagRow}>
      <View style={{ flex: 1 }}>
        <Text style={styles.flagPattern}>
          {PATTERN_LABELS[flag.patternMatched] || flag.patternMatched}
        </Text>
        <Text style={styles.dim}>{formatDate(flag.triggeredAt)}</Text>
        <FlagDetails details={flag.details} pattern={flag.patternMatched} />
      </View>
      {resolution ? (
        <View style={[styles.statusPill, { backgroundColor: resolution.color }]}>
          <Text style={styles.statusPillText}>{resolution.label}</Text>
        </View>
      ) : (
        <TouchableOpacity onPress={onAction} style={styles.reviewBtn}>
          <Text style={styles.reviewBtnText}>Review</Text>
        </TouchableOpacity>
      )}
    </View>
  );
}

function FlagDetails({ details, pattern }) {
  if (!details) return null;
  const items = [];
  if (pattern === 'pattern_1') {
    items.push(`Last deposit: ${formatCurrency(details.lastDepositCents)}`);
    if (details.withdrawalProportion != null) items.push(`Withdrawing ${(details.withdrawalProportion * 100).toFixed(0)}%`);
    if (details.matchesBetween != null) items.push(`${details.matchesBetween} matches between`);
  } else if (pattern === 'pattern_2') {
    items.push(`30d deposits: ${formatCurrency(details.depositTotalCents)}`);
    items.push(`Lifetime matches: ${details.lifetimeMatchCount ?? '-'}`);
  } else if (pattern === 'pattern_3') {
    items.push(`Largest deposit: ${formatCurrency(details.largestDepositCents)}`);
    items.push(`Avg entry: ${formatCurrency(details.avgEntryCents)}`);
    items.push(`Ratio: ${((details.ratio || 0) * 100).toFixed(1)}%`);
  }
  return items.length > 0 ? (
    <Text style={styles.flagDetailText}>{items.join(' · ')}</Text>
  ) : null;
}

// ============================================================================
// STYLES
// ============================================================================
const styles = StyleSheet.create({
  safeArea: { flex: 1, backgroundColor: COLORS.bg },
  container: { padding: 16, paddingBottom: 120 },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: 24, gap: 8 },
  dim: { color: COLORS.textMuted, fontSize: 12 },
  warn: { color: COLORS.danger, fontSize: 16, fontWeight: '600' },
  label: { color: COLORS.text, fontSize: 13, fontWeight: '600', marginBottom: 6 },

  titleRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 12 },
  title: { color: COLORS.text, fontSize: 22, fontWeight: '700' },
  subtitle: { color: COLORS.textMuted, fontSize: 12, marginTop: 4 },
  refreshBtn: { padding: 8 },

  filterRow: { flexDirection: 'row', gap: 8, marginBottom: 16 },
  filterChip: {
    paddingHorizontal: 14, paddingVertical: 8, borderRadius: 999,
    borderWidth: 1, borderColor: COLORS.border, backgroundColor: COLORS.card,
  },
  filterChipActive: { backgroundColor: COLORS.primary, borderColor: COLORS.primary },
  filterChipText: { color: COLORS.textMuted, fontSize: 13 },
  filterChipTextActive: { color: '#000', fontWeight: '700' },

  emptyCard: {
    backgroundColor: COLORS.card, borderRadius: 12, borderWidth: 1, borderColor: COLORS.border,
    padding: 32, alignItems: 'center', gap: 8,
  },
  emptyTitle: { color: COLORS.text, fontSize: 16, fontWeight: '600' },

  accountCard: {
    backgroundColor: COLORS.card, borderRadius: 12, borderWidth: 1, borderColor: COLORS.border,
    padding: 16, marginBottom: 12,
  },
  accountHeader: { flexDirection: 'row', marginBottom: 12 },
  accountName: { color: COLORS.text, fontSize: 16, fontWeight: '600' },
  accountStats: { alignItems: 'flex-end' },
  statusPill: { paddingHorizontal: 8, paddingVertical: 3, borderRadius: 4 },
  statusPillText: { color: '#000', fontSize: 10, fontWeight: '700', textTransform: 'uppercase' },

  kpiRow: {
    flexDirection: 'row', gap: 8, marginBottom: 12,
    paddingTop: 12, borderTopWidth: 1, borderTopColor: COLORS.border,
  },
  kpi: { flex: 1 },
  kpiLabel: { color: COLORS.textMuted, fontSize: 10, textTransform: 'uppercase' },
  kpiValue: { color: COLORS.text, fontSize: 13, fontWeight: '600', marginTop: 2 },

  flagsSection: { marginTop: 12, paddingTop: 12, borderTopWidth: 1, borderTopColor: COLORS.border },
  sectionLabel: {
    color: COLORS.primary, fontSize: 11, fontWeight: '700',
    textTransform: 'uppercase', marginBottom: 8,
  },
  flagRow: {
    flexDirection: 'row', alignItems: 'center', gap: 12,
    paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: COLORS.borderLight,
  },
  flagPattern: { color: COLORS.text, fontSize: 13, fontWeight: '600' },
  flagDetailText: { color: COLORS.textMuted, fontSize: 11, marginTop: 4 },
  reviewBtn: {
    backgroundColor: COLORS.primary, paddingHorizontal: 14, paddingVertical: 8, borderRadius: 6,
  },
  reviewBtnText: { color: '#000', fontWeight: '700', fontSize: 12 },

  modalBackdrop: {
    flex: 1, backgroundColor: 'rgba(0,0,0,0.7)',
    justifyContent: 'center', alignItems: 'center', padding: 20,
  },
  modalCard: {
    backgroundColor: COLORS.card, borderRadius: 12, borderWidth: 1, borderColor: COLORS.border,
    padding: 20, width: '100%', maxWidth: 500,
  },
  modalTitle: { color: COLORS.text, fontSize: 18, fontWeight: '700', marginBottom: 8 },
  notesInput: {
    backgroundColor: COLORS.bg, borderRadius: 6, borderWidth: 1, borderColor: COLORS.border,
    color: COLORS.text, padding: 10, minHeight: 60, marginBottom: 16,
  },
  actionBtnRow: { gap: 8 },
  actionBtn: { paddingVertical: 12, borderRadius: 6, alignItems: 'center' },
  actionBtnHovered: { opacity: 0.85, cursor: 'pointer' },
  actionBtnPressed: { opacity: 0.65 },
  actionBtnText: { color: '#000', fontWeight: '700' },
  cancelBtn: { paddingVertical: 12, alignItems: 'center', marginTop: 8 },
  cancelBtnText: { color: COLORS.textMuted },

  backBtn: { flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 16 },
  backText: { color: COLORS.textMuted },
});
